/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Input, ViewChild, inject, computed } from '@angular/core'
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns'
import { FilterService } from '@progress/kendo-angular-grid'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  FilterOperator,
} from '@progress/kendo-data-query'

import { Enums } from '@cyborg-monorepo/hunter/common'

import { SessionService } from '@hunt/services'

import { BaseCustomFilterComponent } from '../../../../../shared/components/custom-filters/base-custom-filter.component'
import { DashboardStateService } from '../../dashboard-state-service'

@Component({
  standalone: false,
  selector: 'dashboard-tag-filter', // Component selector used in the HTML template
  template: `
    <kendo-multiselect
      #multiselect
      [value]="currentFilterValue"
      [data]="data"
      [field]="field"
      (valueChange)="onChange($event)"
      textField="text"
      valueField="value"
      placeholder="(All)"
    >
      <ng-template kendoMultiSelectItemTemplate let-dataItem>
        @if (dataItem.value) {
          <span
            style="color: transparent"
            class="k-icon k-font-icon k-i-kpi-status-open"
            [ngClass]="dataItem.class"
          ></span>
        }
        {{ dataItem.text }}
      </ng-template>
    </kendo-multiselect>
  `,
  styles: [
    `
      .k-input-solid {
        background-color: var(--background-overlay-default);
        border-radius: var(--corner-radius-tertiary);
        border: var(--stroke-size-primary) solid var(--border-default);
        background: var(--background-function-inset);
      }
    `,
  ],
})
export class DashboardTagFilterComponent extends BaseCustomFilterComponent {
  dashboardStateService = inject(DashboardStateService)

  readonly session = inject(SessionService)
  readonly loaded = computed(
    () => !this.session.loading() && this.session.activeHunt() !== null,
  )
  // Getter for the currently selected filter value
  public get selectedValue(): any {
    const filter = this.filterByField(this.valueField) // Fetches the filter for the valueField
    return filter ? filter.value : null // Returns the filter's value or null if not found
  }

  @ViewChild('multiselect', { read: MultiSelectComponent })
  public select: MultiSelectComponent // References the MultiSelect component in the template

  // Input properties for passing data to the component
  @Input() public filter: CompositeFilterDescriptor // Holds the filter descriptor for the grid
  @Input() public data: {
    text: string
    value: Enums.StatusEnum | Enums.PriorityEnum | Enums.OutcomeEnum
    class: string
  }[] // Data for the dropdown options
  @Input() public field: string // Field to filter by
  @Input() public textField: string // Field used to display the text in the dropdown
  @Input() public valueField: string // Field used as the value in the dropdown

  // Constructor to inject services, optionally inject FilterService and always inject DashboardStateService

  constructor() {
    const filterService = inject(FilterService, { optional: true })

    super(filterService) // Calls the base class constructor with the filterService
  }

  // Getter for retrieving the current filter value
  public get currentFilterValue(): any {
    if (this.filter && this.filter.filters) {
      const filter = (this.filter.filters as FilterDescriptor[]).find(
        ({ field }) => {
          return field === this.field // Find filter related to the field
        },
      )
      const result: { text: string; value: string }[] = [] // Holds the resulting filtered values
      if (filter && filter.value && filter.value.length) {
        filter.value.forEach((value) => {
          result.push({ text: value, value: value }) // Pushes filtered values into result array
        })
      }
      return result // Returns the filtered results
    } else return [] // If no filter is found, return an empty array
  }

  // Method to reset the MultiSelect dropdown to its default state
  public resetDefault(): void {
    this.select.reset() // Resets the MultiSelect component
  }

  // Method to handle the change in selected value
  public onChange(value: any): void {
    const processedValue = value.map((a) => a.value) // Extracts the values from the selected items

    // Find an existing filter for the field
    const filter = (this.filter.filters as FilterDescriptor[]).find(
      ({ field }) => {
        return field === this.field // Check if the current filter matches the field
      },
    )

    // If no value is selected or the processed value is empty, remove the filter
    if (value === null || processedValue.length == 0) {
      this.filter.filters = this.filter.filters.filter(
        (field) => (field as FilterDescriptor).field !== this.field, // Remove the filter for the field
      )
    } else {
      // If a filter exists, update its value
      if (filter) {
        filter.value = processedValue
      } else {
        // Otherwise, add a new filter for the field with the selected value
        this.filter.filters.push({
          field: this.field,
          operator: FilterOperator.LessThanOrEqual, // Uses 'LessThanOrEqual' operator for filtering
          value: processedValue,
        })
      }
    }
  }
}
